import { AutoComplete, Form, Spin } from 'antd';
import { AutoCompleteProps } from 'antd/lib/auto-complete';
import autobind from 'autobind-decorator';
import { observer } from 'mobx-react';
import * as React from 'react';

const FormItem = Form.Item;
const Option = AutoComplete.Option;

interface AutocompleteProps {
	request: DropdownRequest<any>;
	model?: any;
	required?: boolean;
	disabled?: boolean;
	label: string;
	onChanged?: (item: any) => void;
	multi?: boolean;
	onGetErrorMessage?: () => Promise<ValidationError[]>;
}

interface AutocompleteState {
	options: any[],
	errors: ValidationError[];
	fetching?: boolean
	complete?: boolean;
	value?: any;
}


/**
 * Class AutocompleteView
 */
@observer
export class AutocompleteView extends React.Component<AutocompleteProps, AutocompleteState> {

	lastSearchValue: any;

	// For sync between autocomplete UI and searches
	searchCallsStack: number[] = [];
	searchCallId = 0;

	constructor(props) {
		super(props);
		this.state = { options: [], errors: [] };
		this.lastSearchValue = null;
	}

	componentDidMount() {
		this.setState({ value: this.props.model });
		this.loadOptions(this.props.request.query);
	}

	componentWillReceiveProps(nextProps: Readonly<AutocompleteProps>, nextContext: any): void {
		if (nextProps.model !== this.state.value) {
			if (nextProps.model != null && this.state.options.findIndex(o => o.value === nextProps.model) === -1) {
				this.state.options.unshift({ value: nextProps.model, label: nextProps.model, fake: false })
			}
			this.setState(
				{
					value: nextProps.model,
					options: this.state.options.filter(o => !o.fake)
				},
				() => this.lastSearchValue = null
			);
		}
	}

	render() {
		const { value, options, fetching, errors } = this.state;

		let children = options.map((item) => (
			<Option key={item.value} value={item.value}>{item.label}</Option>
		));

		if (fetching) {
			children = children.concat(<Option key='disabled' disabled><Spin size='small' /></Option>);
		}

		const props: AutoCompleteProps = {
			style: { width: '100%' },
			onSelect: this.onSelect,
			onSearch: this.handleSearch,
			backfill: true,
			//onPopupScroll: this.onScroll,
			onBlur: this.onBlur,
			disabled: this.props.disabled,
		}

		if (value != null) {
			props['value'] = value;
		}

		const hasErrors = errors.length !== 0 || (this.props.required && !value);
		let errorMsg = null;
		if (hasErrors) {
			if (this.props.required && value == null) {
				errorMsg = 'Required';
			} else {
				errorMsg = 'Something is wrong' // TODO: errorMsg = this.state.errors.map(e => e.text).join(", ")
			}
		}

		return (
			<FormItem
				label={this.props.label}
				required={this.props.required}
				validateStatus={hasErrors ? 'error' : 'success'}
				help={errorMsg}
			>
				<AutoComplete {...props}>
					{children}
				</AutoComplete>
			</FormItem>
		);
	}

	@autobind
	private async handleSearch(val) {
		this.lastSearchValue = val;
		let currOptions = this.state.options;
		let complete = this.state.complete;

		const idx = ++this.searchCallId;
		this.searchCallsStack.push(idx);

		if (val !== this.props.request.dataSet.searchKeywords) {
			this.setState({ value: undefined, options: [], complete: true, fetching: true });

			this.props.request.dataSet.searchKeywords = val;
			await this.props.request.dataSet.firstPage();

			if (this.searchCallsStack.length === 0 || this.searchCallsStack[this.searchCallsStack.length - 1] !== idx) {
				// this is not the last call so abort
				return;
			}

			currOptions = this.props.request.dataSet.items.map(e => { return { value: e.value, label: e.value, fake: false } });
			complete = this.props.request.dataSet.currentPage === this.props.request.dataSet.totalPages;
		}

		if (!val || currOptions.find(o => !o.fake && o.value.toLowerCase() === val.toLowerCase())) {
			if (this.searchCallsStack.length === 0 || this.searchCallsStack[this.searchCallsStack.length - 1] !== idx) {
				// this is not the last call so abort. check always before render
				return;
			}

			this.setState({ value: undefined, options: currOptions.filter(o => !o.fake), complete, fetching: false })
		} else {
			let fake = currOptions.find(o => o.fake);

			if (!fake) {
				fake = { value: val, label: `Create option "${val}"`, fake: true };
				currOptions.unshift(fake);
			} else {
				fake.value = val;
				fake.label = `Create option "${val}"`;
			}

			if (this.searchCallsStack.length === 0 || this.searchCallsStack[this.searchCallsStack.length - 1] !== idx) {
				// this is not the last call so abort. check always before render
				return;
			}

			this.setState({ value: undefined, options: currOptions, complete, fetching: false });
		}
	}

	@autobind
	private onBlur() {
		const { value, options } = this.state;
		const lastSearch = this.lastSearchValue;

		this.searchCallsStack = [];
		if (this.searchCallId > Number.MAX_SAFE_INTEGER - 100000) { this.searchCallId = 0; }

		if (this.props.request.dataSet.searchKeywords) {
			this.props.request.dataSet.searchKeywords = null;
			this.props.request.dataSet.firstPage().then(() => {
				const newOptions = this.props.request.dataSet.items.map(e => { return { value: e.value, label: e.value, fake: false } });

				const valueOption = options.find(o => o.value === value);
				if (valueOption && newOptions.findIndex(o => o.value === value) === -1) {
					newOptions.unshift(valueOption);
				}

				this.setState({
					options: newOptions,
					complete: this.props.request.dataSet.currentPage === this.props.request.dataSet.totalPages
				},
					() => {
						if (lastSearch != null && value !== lastSearch) {
							if (lastSearch === '') {
								this.onSelect(null);
							} else {
								this.onSelect(lastSearch);
							}
						}
					})
			});
		} else {
			if (lastSearch != null && value !== lastSearch) {
				if (lastSearch === '') {
					this.onSelect(null);
				} else {
					this.onSelect(lastSearch);
				}
			}
		}
	}

	@autobind
	private async onSelect(newValue) {
		this.props.onChanged({ value: newValue });

		const option = this.state.options.find(o => o.value === newValue);

		if (option && option.fake) {
			option.fake = false;
			option.label = option.value
		} else if (!option && newValue) {
			this.state.options.unshift({ value: newValue, label: newValue, fake: false });
		}

		if (this.props.onGetErrorMessage != null) {
			const errors = await this.props.onGetErrorMessage();
			this.setState({
				errors: errors,
				value: newValue,
				options: this.state.options.filter(o => !o.fake)
			},
				() => { this.lastSearchValue = null })
		} else {
			this.setState({
				value: newValue,
				options: this.state.options.filter(o => !o.fake)
			},
				() => { this.lastSearchValue = null })
		}
	}

	@autobind
	private loadOptions(request: () => Promise<any>) {
		return request().then(res => {
			const options = this.props.request.dataSet.items.map(e => { return { value: e.value, label: e.value, fake: false } });

			if (this.props.model != null && options.findIndex(o => o.value === this.props.model) === -1) {
				options.unshift({ value: this.props.model, label: this.props.model, fake: false });
			}

			const state = {
				options: options,
				complete: this.props.request.dataSet.currentPage === this.props.request.dataSet.totalPages
			};

			this.setState(state);
		});
	}

	@autobind
	private appendOptions(request: () => Promise<any>): Promise<void> {
		this.setState({ fetching: true });

		return request().then(res => {
			const newOptions = this.props.request.dataSet.items.map(e => { return { value: e.value, label: e.value, fake: false } });

			const toConcat = [];

			for (const opt of newOptions) {
				const o = this.state.options.find((item) => item.value === opt.value);
				if (o) {
					o.fake = false;
					o.label = o.value;
				} else {
					toConcat.push(opt)
				}
			}

			const options = this.state.options.concat(toConcat);

			const state = {
				options: options,
				complete: this.props.request.dataSet.currentPage === this.props.request.dataSet.totalPages,
				fetching: false
			};
			this.setState(state);
		});
	}

	//@autobind
	//private onScroll(e) {
	//	if (!this.state.fetching && !this.state.complete && e.target.scrollTop + e.target.clientHeight + 5 >= e.target.scrollHeight) {
	//		this.appendOptions(this.props.request.dataSet.nextPage);
	//	}
	//}
}



// WEBPACK FOOTER //
// ./src/core/ui/autocomplete/autocomplete.view.tsx